import type { FieldValidator, FormBuilderConfig, FormElementConfig } from '@sp-saljapro/form-builder';

import type { DetailsCustomerData, DetailsTranslations } from '../../types/customer-information.types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;
const PHONE_REGEX = /^[+]?[\d\s-]{7,15}$/;

interface BuildCustomerInformationDetailsFormConfigOptions {
    readonly customerData: DetailsCustomerData;
    readonly readonlyMode: boolean;
    readonly translations?: DetailsTranslations | undefined;
}

const getValidators = ({
    missingMessage,
    invalidMessage,
    pattern,
    readonlyMode,
}: {
    readonly missingMessage: string;
    readonly invalidMessage: string;
    readonly pattern: RegExp;
    readonly readonlyMode: boolean;
}): ReadonlyArray<FieldValidator<string>> =>
    readonlyMode
        ? []
        : [
              { type: 'required', message: missingMessage },
              { type: 'pattern', pattern, message: invalidMessage },
          ];

export const buildCustomerInformationDetailsFormConfig = ({
    customerData,
    readonlyMode,
    translations,
}: BuildCustomerInformationDetailsFormConfigOptions) => {
    const elements = [
        {
            id: 'firstName',
            type: 'text',
            label: translations?.firstNameLabel ?? 'First Name',
            readOnly: readonlyMode,
            hidden: readonlyMode && !customerData.firstName,
        },
        {
            id: 'lastName',
            type: 'text',
            label: translations?.lastNameLabel ?? 'Last Name',
            readOnly: readonlyMode,
            hidden: readonlyMode && !customerData.lastName,
        },
        {
            id: 'businessName',
            type: 'text',
            label: translations?.businessNameLabel ?? 'Business Name',
            readOnly: readonlyMode,
            hidden: !customerData.businessName,
        },
        {
            id: 'email',
            type: 'email',
            label: translations?.emailLabel ?? 'Email',
            readOnly: readonlyMode,
            hidden: readonlyMode && !customerData.email,
            validators: getValidators({
                missingMessage: translations?.emailMissingErrorLabel ?? '',
                invalidMessage: translations?.emailInvalidErrorLabel ?? '',
                pattern: EMAIL_REGEX,
                readonlyMode,
            }),
        },
        {
            id: 'mobile',
            type: 'text',
            label: translations?.phoneLabel ?? 'Phone',
            readOnly: readonlyMode,
            hidden: readonlyMode && !customerData.mobile,
            validators: getValidators({
                missingMessage: translations?.phoneMissingErrorLabel ?? '',
                invalidMessage: translations?.phoneInvalidErrorLabel ?? '',
                pattern: PHONE_REGEX,
                readonlyMode,
            }),
        },
        {
            id: 'addressLine1',
            type: 'text',
            label: translations?.addressLine1Label ?? 'Address Line 1',
            readOnly: readonlyMode,
            hidden: readonlyMode && !customerData.addressLine1,
        },
        {
            id: 'addressLine2',
            type: 'text',
            label: translations?.addressLine2Label ?? 'Address Line 2',
            readOnly: readonlyMode,
            hidden: readonlyMode && !customerData.addressLine2,
        },
        {
            id: 'zipCode',
            type: 'text',
            label: translations?.zipCodeLabel ?? 'Zip Code',
            readOnly: readonlyMode,
            hidden: readonlyMode && !customerData.zipCode,
        },
        {
            id: 'city',
            type: 'text',
            label: translations?.cityLabel ?? 'City',
            readOnly: readonlyMode,
            hidden: readonlyMode && !customerData.city,
        },
    ] satisfies readonly FormElementConfig[];

    return {
        id: 'customer-information-details',
        elements,
        defaultValues: {
            firstName: customerData.firstName ?? '',
            lastName: customerData.lastName ?? '',
            businessName: customerData.businessName ?? '',
            email: customerData.email ?? '',
            mobile: customerData.mobile ?? '',
            addressLine1: customerData.addressLine1 ?? '',
            addressLine2: customerData.addressLine2 ?? '',
            zipCode: customerData.zipCode ?? '',
            city: customerData.city ?? '',
        },
    } satisfies FormBuilderConfig<typeof elements>;
};
